<template>
  <div class="service-records">
    <div class="service-records__title">{{ recordsListTitle }}</div>

    <!-- Error State -->
    <div v-if="hasError" class="service-records__error">
      <div class="error-message">
        <i class="pi pi-exclamation-triangle" />
        <p>{{ props.error }}</p>
      </div>
    </div>

    <!-- Loading State -->
    <div v-else-if="isLoading" class="service-records__loader">
      <b-spinner class="loader" type="grow" small />
      <span class="loading-text">Laster servicerapporter...</span>
    </div>

    <!-- Content State -->
    <template v-else>
      <div v-if="hasServiceRecords" class="service-records__content">
        <ServiceRecordListItem
          v-for="formattedRecord in formattedServiceRecords"
          :key="formattedRecord.id"
          :record="formattedRecord"
          :is-expanded="state.collapsedRecordId === formattedRecord.id"
          class="service-records__record"
          @item-clicked="handleRecordClick"
          @delete-clicked="handleDeleteClick"
          @edit-clicked="handleEditClick"
          @mark-complete-clicked="handleMarkCompleteClick"
        >
          <!-- eslint-disable-next-line @typescript-eslint/ban-ts-comment -->
          <!-- @ts-ignore: Vue slot type inference limitation -->
          <template #collapse-content>
            <ServiceRecord
              :service-record="formattedRecord.originalServiceRecord"
              @mark-complete="handleMarkCompleteFromRecord"
            />
          </template>
        </ServiceRecordListItem>
      </div>

      <!-- Empty State -->
      <ServiceRecordsEmptyState
        v-else
        title="Ingen servicerapporter registrert"
        subtitle="Legg til din første servicerapport ved å klikke på 'Legg til servicerapport' i Servicerapporter-fanen over."
        :header-icon-url="NoServiceMessageIcon"
      />
    </template>
  </div>
</template>

<script setup lang="ts">
/**
 * ServiceRecordsList Component
 *
 * Displays a list of service records with loading, error, and empty states.
 *
 * Refactored to follow Vue.js best practices:
 * - Improved loading state logic with support for explicit loading prop
 * - Added proper error handling and display
 * - Enhanced type safety with better validation
 * - Extracted business logic into separate functions
 * - Added defensive programming practices for event handlers
 * - Improved accessibility and user feedback
 */
import { computed, reactive } from "vue";
import { formatDateInNorwegian } from "@/utilities/dateUtils";
import ServiceRecord from "./ServiceRecord.vue";
import ServiceRecordsEmptyState from "./ServiceRecordsEmptyState.vue";
import ServiceRecordListItem from "./ServiceRecordListItem.vue";
import type {
  ServiceRecord as ServiceRecordType,
  ServiceRecordListItem as ServiceRecordListItemType,
} from "@/types/service-records";
import NoServiceMessageIcon from "@/assets/img/document.svg";

// Props interface
interface Props {
  tenderId: number;
  customerOrgNumber: string;
  serviceOfferId: number;
  supplierId: number;
  serviceRecords: ServiceRecordType[] | null;
  isLoading?: boolean;
  error?: string | null;
}

// Emits interface
interface Emits {
  (e: "delete-service-record", recordId: number): void;
  (e: "update-service-record", record: ServiceRecordType): void;
  (e: "mark-complete", recordId: number): void;
}

const props = defineProps<Props>();
const emit = defineEmits<Emits>();

// Reactive state
const state = reactive({
  collapsedRecordId: null as string | null,
});

// Computed properties - Following Vue best practices for loading states
const isLoading = computed(() => {
  // Prioritize explicit loading prop if provided
  if (props.isLoading !== undefined) {
    return props.isLoading;
  }
  // Fallback to null check for backward compatibility
  return props.serviceRecords === null;
});

const hasError = computed(() => Boolean(props.error));

const hasServiceRecords = computed(() => {
  return Boolean(props.serviceRecords?.length);
});

const recordsListTitle = computed(() => {
  if (isLoading.value) return "Laster servicerapporter...";
  if (hasError.value) return "Feil ved lasting av servicerapporter";

  const count = props.serviceRecords?.length ?? 0;
  return count === 1 ? "1 servicerapport" : `${count} servicerapporter`;
});

const formattedServiceRecords = computed((): ServiceRecordListItemType[] => {
  if (!props.serviceRecords?.length) return [];

  return [...props.serviceRecords]
    .sort(
      (a, b) =>
        new Date(b.serviceDate).getTime() - new Date(a.serviceDate).getTime(),
    )
    .map(transformServiceRecord);
});

// Helper function to transform service record - Following single responsibility principle
const transformServiceRecord = (
  record: ServiceRecordType,
): ServiceRecordListItemType => {
  const serviceDate = formatDateInNorwegian(record.serviceDate, "DD.MM.YYYY");
  // Use serviceState from API, fallback to status for backward compatibility
  const status = record.serviceState || record.status;
  const statusText = status === "Completed" ? "Fullført" : "Pågående";

  let nextServiceInfo: string | null = null;
  if (record.nextServiceDate) {
    const nextDate = formatDateInNorwegian(record.nextServiceDate, "MM.YYYY");
    nextServiceInfo = `Neste service: ${nextDate}`;
  }

  return {
    id: record.id.toString(),
    title: `Service utført ${serviceDate}`,
    subtitle: record.comment,
    nextServiceInfo,
    status: status,
    statusText,
    originalServiceRecord: record,
  };
};

// Event handlers - Improved error handling and validation
const handleRecordClick = (recordId: string) => {
  if (!recordId) return;

  if (state.collapsedRecordId === recordId) {
    state.collapsedRecordId = null;
  } else {
    state.collapsedRecordId = recordId;
  }
};

const handleDeleteClick = (recordId: string) => {
  if (!recordId) return;

  const numericId = parseInt(recordId, 10);
  if (isNaN(numericId)) {
    console.warn("Invalid record ID for deletion:", recordId);
    return;
  }

  emit("delete-service-record", numericId);
};

const handleEditClick = (recordId: string) => {
  if (!recordId || !props.serviceRecords) return;

  const record = props.serviceRecords.find((r) => r.id.toString() === recordId);
  if (!record) {
    console.warn("Record not found for editing:", recordId);
    return;
  }

  emit("update-service-record", record);
};

const handleMarkCompleteClick = (recordId: string) => {
  if (!recordId) return;

  const numericId = parseInt(recordId, 10);
  if (isNaN(numericId)) {
    console.warn("Invalid record ID for completion:", recordId);
    return;
  }

  emit("mark-complete", numericId);
};

const handleMarkCompleteFromRecord = (recordId: number) => {
  if (typeof recordId !== "number" || recordId <= 0) {
    console.warn("Invalid record ID from record component:", recordId);
    return;
  }

  emit("mark-complete", recordId);
};
</script>

<style lang="scss" scoped>
.service-records {
  width: 100%;

  &__title {
    font-size: 1.25rem;
    font-weight: 600;
    color: $color-primary-darker;
    margin-bottom: 1.5rem;
    padding-bottom: 0.75rem;
    border-bottom: 2px solid $color-primary-light;

    @media (max-width: $bp-sm) {
      font-size: 1.125rem;
      margin-bottom: 1rem;
    }
  }

  &__content {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
  }

  &__record {
    &:last-child {
      margin-bottom: 0;
    }
  }

  &__error {
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 3rem;

    .error-message {
      display: flex;
      flex-direction: column;
      align-items: center;
      text-align: center;
      color: $color-error;

      i {
        font-size: 2rem;
        margin-bottom: 1rem;
      }

      p {
        margin: 0;
        font-weight: 500;
      }
    }
  }

  &__loader {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    padding: 3rem;
    color: $color-primary;

    .loader {
      color: $color-primary;
      margin-bottom: 1rem;
    }

    .loading-text {
      font-size: 0.875rem;
      opacity: 0.8;
    }
  }
}
</style>
